import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';

import PhotoUploader from '../../../components/PhotoUploader';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import {
    createSiteInspection,
    getSiteInspection,
    updateSiteInspection
} from './siteInspectionApi';
import {
    buildEmptyInspectionChecklist,
    INSPECTION_CHECKLIST_OPTIONS,
    INSPECTION_RESULT_OPTIONS,
    INSPECTION_STATUS_META
} from './siteInspectionConstants';
import './siteInspectionDialog.css';

const MACHINE_TYPE_OPTIONS = [
    { value: '', es: 'Sin definir', en: 'Not defined' },
    { value: 'coffee', es: 'Café', en: 'Coffee' },
    { value: 'snack', es: 'Snack', en: 'Snack' },
    { value: 'drink', es: 'Bebidas', en: 'Drink' },
    { value: 'combo', es: 'Combo', en: 'Combo' },
    { value: 'dispenser', es: 'Dispensador', en: 'Dispenser' },
    { value: 'other', es: 'Otro', en: 'Other' }
];

const emptyForm = (currentAdmin = {}) => ({
    _id: '',
    inspectionNumber: '',
    status: 'pending',
    result: 'pending',
    client: {
        name: '',
        locationName: '',
        address: '',
        specificArea: '',
        mapUrl: '',
        contactName: '',
        contactPhone: ''
    },
    scheduledAt: new Date(),
    assignedTechnicianId: currentAdmin?._id || '',
    assignedTechnician: null,
    accessInstructions: '',
    plannedEquipment: { machineType: '', preferredModel: '', dimensionsNotes: '' },
    checklist: buildEmptyInspectionChecklist(),
    checklistProgress: 0,
    generalPhotos: [],
    overallNotes: '',
    adjustmentSummary: '',
    linkedMovement: null
});

const normalizeRole = (value) => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
const personName = (person) =>
    `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || person?.email || '';

const SiteInspectionDialog = forwardRef(({
    technicians = [],
    currentAdmin = {},
    onChanged,
    onCreateInstallation
}, ref) => {
    const dispatch = useDispatch();
    const language = usePreferredLanguage();
    const isSpanish = language === 'es';
    const text = (en, es) => (isSpanish ? es : en);
    const role = normalizeRole(currentAdmin?.role?.uid || currentAdmin?.role?.title);
    const isTechnician = role === 'technician';
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(() => emptyForm(currentAdmin));

    const localize = (options) => options.map((option) => ({
        ...option,
        label: isSpanish ? option.es : option.en
    }));
    const checklistOptions = localize(INSPECTION_CHECKLIST_OPTIONS);
    const resultOptions = localize(INSPECTION_RESULT_OPTIONS);
    const machineTypeOptions = localize(MACHINE_TYPE_OPTIONS);
    const readOnly = ['completed', 'cancelled'].includes(form.status);
    const currentChecklistItems = form.checklist.flatMap((section) => section.items || []);
    const displayedProgress = currentChecklistItems.length
        ? Math.round((currentChecklistItems.filter((item) => item.value !== 'pending').length / currentChecklistItems.length) * 100)
        : 0;

    const mapRecordToForm = (record) => ({
        ...emptyForm(currentAdmin),
        ...record,
        _id: record?._id || '',
        scheduledAt: record?.scheduledAt ? new Date(record.scheduledAt) : new Date(),
        assignedTechnicianId: record?.assignedTechnician?._id || record?.assignedTechnician || '',
        client: { ...emptyForm(currentAdmin).client, ...(record?.client || {}) },
        plannedEquipment: {
            ...emptyForm(currentAdmin).plannedEquipment,
            ...(record?.plannedEquipment || {})
        },
        checklist: Array.isArray(record?.checklist) && record.checklist.length
            ? record.checklist
            : buildEmptyInspectionChecklist(),
        generalPhotos: Array.isArray(record?.generalPhotos) ? record.generalPhotos : []
    });

    useImperativeHandle(ref, () => ({
        openCreate: () => {
            setForm(emptyForm(currentAdmin));
            setVisible(true);
        },
        openDetails: async (record) => {
            setVisible(true);
            setLoading(true);
            try {
                const response = await getSiteInspection(record.siteInspectionId || record._id, dispatch);
                if (response?.data) setForm(mapRecordToForm(response.data));
            } finally {
                setLoading(false);
            }
        }
    }));

    const updateClient = (field, value) => setForm((current) => ({
        ...current,
        client: { ...current.client, [field]: value }
    }));
    const updateEquipment = (field, value) => setForm((current) => ({
        ...current,
        plannedEquipment: { ...current.plannedEquipment, [field]: value }
    }));
    const updateChecklistItem = (sectionKey, itemKey, patch) => setForm((current) => ({
        ...current,
        checklist: current.checklist.map((section) => section.key !== sectionKey ? section : ({
            ...section,
            items: section.items.map((item) => item.key !== itemKey ? item : ({ ...item, ...patch }))
        }))
    }));

    const addGeneralPhoto = (photo) => setForm((current) => {
        if (!photo || current.generalPhotos.length >= 5) return current;
        return { ...current, generalPhotos: [...current.generalPhotos, photo] };
    });
    const addItemPhoto = (sectionKey, itemKey, photo) => {
        if (!photo) return;
        const item = form.checklist
            .find((section) => section.key === sectionKey)?.items
            .find((entry) => entry.key === itemKey);
        if ((item?.photos || []).length >= 3) return;
        updateChecklistItem(sectionKey, itemKey, { photos: [...(item?.photos || []), photo] });
    };

    const validateBase = () => {
        if (!form.client.name.trim() || !form.client.locationName.trim() || !form.client.address.trim()) {
            toast.warn(text(
                'Enter the client, location name and address.',
                'Ingrese el cliente, la ubicación y la dirección.'
            ));
            return false;
        }
        if (!form.scheduledAt) {
            toast.warn(text('Select the inspection date.', 'Seleccione la fecha de inspección.'));
            return false;
        }
        return true;
    };

    const validateCompletion = () => {
        const items = form.checklist.flatMap((section) => section.items || []);
        const pending = items.filter((item) => item.value === 'pending');
        if (pending.length) {
            toast.warn(text(
                `${pending.length} checklist items are still pending.`,
                `Faltan ${pending.length} puntos por completar.`
            ));
            return false;
        }
        const missingEvidence = items.filter((item) =>
            item.value === 'no' && (!String(item.note || '').trim() || !(item.photos || []).length)
        );
        if (missingEvidence.length) {
            toast.warn(text(
                'Every non-compliant item needs a comment and photo.',
                'Cada punto que no cumple necesita comentario y fotografía.'
            ));
            return false;
        }
        if (form.result === 'pending') {
            toast.warn(text('Select the final result.', 'Seleccione el resultado final.'));
            return false;
        }
        if (items.some((item) => item.value === 'no') && form.result === 'suitable') {
            toast.warn(text(
                'A site with non-compliant items cannot be marked suitable without observations.',
                'Una ubicación con incumplimientos no puede marcarse como apta sin observaciones.'
            ));
            return false;
        }
        if (['suitable_with_adjustments', 'not_suitable'].includes(form.result) && !form.adjustmentSummary.trim()) {
            toast.warn(text(
                'Describe the required adjustments or reason.',
                'Describa las adecuaciones requeridas o la razón del resultado.'
            ));
            return false;
        }
        return true;
    };

    const makePayload = (status) => ({
        client: form.client,
        scheduledAt: form.scheduledAt,
        assignedTechnicianId: isTechnician ? currentAdmin?._id : (form.assignedTechnicianId || null),
        accessInstructions: form.accessInstructions,
        plannedEquipment: form.plannedEquipment,
        checklist: form.checklist.map((section) => ({
            key: section.key,
            items: (section.items || []).map((item) => ({
                key: item.key,
                value: item.value,
                note: item.note,
                photos: item.photos || []
            }))
        })),
        generalPhotos: form.generalPhotos,
        overallNotes: form.overallNotes,
        adjustmentSummary: form.adjustmentSummary,
        result: status === 'completed' ? form.result : 'pending',
        status
    });

    const save = async (status) => {
        if (saving || !validateBase()) return;
        if (status === 'completed' && !validateCompletion()) return;
        setSaving(true);
        try {
            const payload = makePayload(status);
            const response = form._id
                ? await updateSiteInspection(form._id, payload, dispatch)
                : await createSiteInspection(payload, dispatch);
            if ([200, 201].includes(response?.status)) {
                const saved = response?.data?.data;
                if (saved) setForm(mapRecordToForm(saved));
                toast.success(status === 'completed'
                    ? text('Site inspection completed.', 'Inspección de sitio completada.')
                    : text('Inspection progress saved.', 'Progreso de la inspección guardado.'));
                if (onChanged) await onChanged();
                if (status !== 'completed') setVisible(false);
            }
        } finally {
            setSaving(false);
        }
    };

    const statusMeta = INSPECTION_STATUS_META[form.status] || INSPECTION_STATUS_META.pending;
    const footer = (
        <div className="flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
                {readOnly &&
                    !form.linkedMovement &&
                    ['suitable', 'suitable_with_adjustments'].includes(form.result) && (
                        <Button
                            label={text('Create installation movement', 'Crear movimiento de instalación')}
                            icon="pi pi-arrow-right"
                            className="p-button-success"
                            onClick={() => {
                                setVisible(false);
                                onCreateInstallation?.(form);
                            }}
                        />
                    )}
            </div>
            <div className="flex gap-2">
                <Button
                    label={text('Close', 'Cerrar')}
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={() => setVisible(false)}
                    disabled={saving}
                />
                {!readOnly && (
                    <>
                        <Button
                            label={text('Save progress', 'Guardar progreso')}
                            icon="pi pi-save"
                            className="p-button-outlined"
                            loading={saving}
                            onClick={() => save('in_progress')}
                        />
                        <Button
                            label={text('Complete inspection', 'Completar inspección')}
                            icon="pi pi-check"
                            loading={saving}
                            onClick={() => save('completed')}
                        />
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            header={form._id
                ? `${text('Site inspection', 'Inspección de sitio')} ${form.inspectionNumber || ''}`
                : text('New site inspection', 'Nueva inspección de sitio')}
            visible={visible}
            modal
            maximizable
            style={{ width: 'min(96vw, 78rem)' }}
            className="site-inspection-dialog"
            onHide={() => setVisible(false)}
            footer={footer}
        >
            {loading ? (
                <div className="text-500 p-4">{text('Loading inspection...', 'Cargando inspección...')}</div>
            ) : (
                <div className="grid p-fluid">
                    <div className="col-12 flex justify-content-between align-items-center">
                        <div>
                            <h4 className="m-0">{text('Pre-installation site review', 'Revisión del sitio previa a instalación')}</h4>
                            <small className="text-600">{text(
                                'This evaluates the physical location; assigning a machine is not required.',
                                'Esta evaluación corresponde a la ubicación física; no requiere asignar una máquina.'
                            )}</small>
                        </div>
                        <Tag value={isSpanish ? statusMeta.es : statusMeta.en} severity={statusMeta.severity} />
                    </div>

                    <div className="col-12"><h5>{text('Location information', 'Información de la ubicación')}</h5></div>
                    <div className="col-12 md:col-4">
                        <label>{text('Client', 'Cliente')} *</label>
                        <InputText value={form.client.name} onChange={(e) => updateClient('name', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Location or branch', 'Ubicación o sucursal')} *</label>
                        <InputText value={form.client.locationName} onChange={(e) => updateClient('locationName', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Specific area', 'Área específica')}</label>
                        <InputText value={form.client.specificArea || ''} onChange={(e) => updateClient('specificArea', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-8">
                        <label>{text('Address', 'Dirección')} *</label>
                        <InputText value={form.client.address} onChange={(e) => updateClient('address', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>Google Maps</label>
                        <InputText value={form.client.mapUrl || ''} onChange={(e) => updateClient('mapUrl', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Contact person', 'Persona de contacto')}</label>
                        <InputText value={form.client.contactName || ''} onChange={(e) => updateClient('contactName', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Contact phone', 'Teléfono')}</label>
                        <InputText value={form.client.contactPhone || ''} onChange={(e) => updateClient('contactPhone', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Inspection date and time', 'Fecha y hora de inspección')} *</label>
                        <Calendar value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.value })} showIcon showTime hourFormat="12" disabled={readOnly} />
                    </div>
                    {!isTechnician && (
                        <div className="col-12 md:col-4">
                            <label>{text('Assigned technician', 'Técnico responsable')}</label>
                            <Dropdown
                                value={form.assignedTechnicianId}
                                options={technicians}
                                optionLabel="label"
                                optionValue="_id"
                                onChange={(e) => setForm({ ...form, assignedTechnicianId: e.value })}
                                placeholder={text('Unassigned', 'Por asignar')}
                                showClear
                                filter
                                disabled={readOnly}
                            />
                        </div>
                    )}
                    {isTechnician && (
                        <div className="col-12 md:col-4">
                            <label>{text('Technician', 'Técnico')}</label>
                            <InputText value={personName(form.assignedTechnician) || personName(currentAdmin)} disabled />
                        </div>
                    )}
                    <div className="col-12">
                        <label>{text('Access instructions', 'Instrucciones de ingreso')}</label>
                        <InputTextarea rows={2} value={form.accessInstructions || ''} onChange={(e) => setForm({ ...form, accessInstructions: e.target.value })} disabled={readOnly} />
                    </div>

                    <div className="col-12"><h5>{text('Planned equipment (optional)', 'Equipo previsto (opcional)')}</h5></div>
                    <div className="col-12 md:col-4">
                        <label>{text('Equipment category', 'Categoría del equipo')}</label>
                        <Dropdown value={form.plannedEquipment.machineType} options={machineTypeOptions} onChange={(e) => updateEquipment('machineType', e.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Suggested model', 'Modelo sugerido')}</label>
                        <InputText value={form.plannedEquipment.preferredModel || ''} onChange={(e) => updateEquipment('preferredModel', e.target.value)} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-4">
                        <label>{text('Dimensions or space notes', 'Dimensiones o notas de espacio')}</label>
                        <InputText value={form.plannedEquipment.dimensionsNotes || ''} onChange={(e) => updateEquipment('dimensionsNotes', e.target.value)} disabled={readOnly} />
                    </div>

                    <div className="col-12">
                        <div className="site-inspection-progress">
                            <span>{text('Checklist progress', 'Progreso de la inspección')}</span>
                            <strong>{displayedProgress}%</strong>
                        </div>
                    </div>
                    {form.checklist.map((section) => (
                        <div className="col-12" key={section.key}>
                            <Card className="site-inspection-section">
                                <h5 className="mt-0">{section.label}</h5>
                                {(section.items || []).map((item) => (
                                    <div className="site-inspection-item" key={item.key}>
                                        <div className="site-inspection-item-main">
                                            <span>{item.label}</span>
                                            <Dropdown
                                                value={item.value}
                                                options={checklistOptions}
                                                onChange={(e) => updateChecklistItem(section.key, item.key, { value: e.value })}
                                                disabled={readOnly}
                                            />
                                        </div>
                                        {item.value === 'no' && (
                                            <div className="grid p-fluid mt-2">
                                                <div className="col-12 md:col-7">
                                                    <label>{text('Finding and required action', 'Hallazgo y acción requerida')} *</label>
                                                    <InputTextarea rows={2} value={item.note || ''} onChange={(e) => updateChecklistItem(section.key, item.key, { note: e.target.value })} disabled={readOnly} />
                                                </div>
                                                <div className="col-12 md:col-5">
                                                    {!readOnly && (
                                                        <PhotoUploader
                                                            value=""
                                                            onChange={(photo) => addItemPhoto(section.key, item.key, photo)}
                                                            maxUploadBytes={300 * 1024}
                                                            maxDimension={1280}
                                                            helperText={text('Required. Up to 3 photos.', 'Obligatoria. Hasta 3 fotos.')}
                                                        />
                                                    )}
                                                    {(item.photos || []).length > 0 && (
                                                        <div className="site-inspection-photo-list">
                                                            {item.photos.map((photo, index) => (
                                                                <div key={`${item.key}-${index}`}>
                                                                    <img src={photo} alt={`${item.label} ${index + 1}`} />
                                                                    {!readOnly && <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-sm" onClick={() => updateChecklistItem(section.key, item.key, { photos: item.photos.filter((_, photoIndex) => photoIndex !== index) })} />}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </Card>
                        </div>
                    ))}

                    <div className="col-12"><h5>{text('Evidence and result', 'Evidencia y resultado')}</h5></div>
                    <div className="col-12">
                        {!readOnly && (
                            <PhotoUploader value="" onChange={addGeneralPhoto} maxUploadBytes={300 * 1024} maxDimension={1280} helperText={text('Optional. Up to 5 overview photos.', 'Opcional. Hasta 5 fotografías generales.')} />
                        )}
                        {form.generalPhotos.length > 0 && (
                            <div className="site-inspection-photo-list mt-2">
                                {form.generalPhotos.map((photo, index) => (
                                    <div key={`general-${index}`}>
                                        <img src={photo} alt={`${text('Site', 'Sitio')} ${index + 1}`} />
                                        {!readOnly && <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-sm" onClick={() => setForm({ ...form, generalPhotos: form.generalPhotos.filter((_, photoIndex) => photoIndex !== index) })} />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="col-12 md:col-6">
                        <label>{text('Final result', 'Resultado final')}</label>
                        <Dropdown value={form.result} options={resultOptions} onChange={(e) => setForm({ ...form, result: e.value })} disabled={readOnly} />
                    </div>
                    <div className="col-12 md:col-6">
                        <label>{text('Required adjustments or reason', 'Adecuaciones requeridas o razón')}</label>
                        <InputTextarea rows={3} value={form.adjustmentSummary || ''} onChange={(e) => setForm({ ...form, adjustmentSummary: e.target.value })} disabled={readOnly} />
                    </div>
                    <div className="col-12">
                        <label>{text('General comments', 'Comentarios generales')}</label>
                        <InputTextarea rows={3} value={form.overallNotes || ''} onChange={(e) => setForm({ ...form, overallNotes: e.target.value })} disabled={readOnly} />
                    </div>
                </div>
            )}
        </Dialog>
    );
});

export default SiteInspectionDialog;
